import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Users, UserCheck, AlertTriangle, CheckCircle, 
  ChevronRight, Clock, Building2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

import API from '@/config/api';
// API imported from config;

const StaffRatioWidget = () => {
  const { getAuthHeaders } = useAuth();
  const [ratioData, setRatioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRatioData();
  }, []);

  const fetchRatioData = async () => {
    try {
      const response = await axios.get(`${API}/classrooms/ratios`, getAuthHeaders());
      setRatioData(response.data);
    } catch (err) {
      setError('Failed to load staffing data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-clay">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/3"></div>
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="card-clay">
        <CardContent className="p-6 text-center text-muted-foreground">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
          <p>{error}</p>
        </CardContent>
      </Card>
    );
  }

  const { classrooms = [], summary = {} } = ratioData || {};
  const compliantCount = classrooms.filter(c => c.is_compliant).length;
  const totalClassrooms = classrooms.length;
  const complianceRate = totalClassrooms > 0 ? Math.round((compliantCount / totalClassrooms) * 100) : 0;

  // Get all staff working today (dedupe by id when available, else by name)
  const allStaffToday = new Set();
  classrooms.forEach(c => {
    if (c.staff_members && c.staff_members.length > 0) {
      c.staff_members.forEach(m => allStaffToday.add(m.id || m.name));
    } else {
      (c.staff_names || []).forEach(name => allStaffToday.add(name));
    }
  });

  return (
    <Card className="card-clay" data-testid="staff-ratio-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Today's Staffing Overview
          </CardTitle>
          <Badge 
            variant={complianceRate === 100 ? "default" : "destructive"}
            className={complianceRate === 100 ? "bg-green-100 text-green-700" : ""}
          >
            {complianceRate}% Compliant
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-blue-50 rounded-xl">
            <p className="text-2xl font-bold text-blue-600">{allStaffToday.size}</p>
            <p className="text-xs text-blue-600/70">Staff Working</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-xl">
            <p className="text-2xl font-bold text-green-600">{compliantCount}</p>
            <p className="text-xs text-green-600/70">Rooms OK</p>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-xl">
            <p className="text-2xl font-bold text-amber-600">{totalClassrooms - compliantCount}</p>
            <p className="text-xs text-amber-600/70">Need Staff</p>
          </div>
        </div>

        {/* Compliance Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">DCF Ratio Compliance</span>
            <span className="font-medium">{compliantCount}/{totalClassrooms} classrooms</span>
          </div>
          <Progress 
            value={complianceRate} 
            className={`h-2 ${complianceRate === 100 ? '[&>div]:bg-green-500' : '[&>div]:bg-amber-500'}`}
          />
        </div>

        {/* Classroom List */}
        <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
          {classrooms.map((classroom) => (
            <div 
              key={classroom.classroom_id}
              className={`p-3 rounded-xl border-l-4 transition-all hover:shadow-sm ${
                classroom.is_compliant 
                  ? 'bg-green-50/50 border-l-green-500' 
                  : 'bg-red-50/50 border-l-red-500'
              }`}
              data-testid={`classroom-ratio-${classroom.classroom_id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{classroom.classroom_name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">{classroom.age_range}</p>
                </div>
                {classroom.is_compliant ? (
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-xs ml-6">
                <div>
                  <span className="text-muted-foreground">Children:</span>
                  <span className="font-medium ml-1">{classroom.enrolled_children}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Staff:</span>
                  <span className="font-medium ml-1">{classroom.scheduled_staff}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Ratio:</span>
                  <span className={`font-medium ml-1 ${classroom.is_compliant ? 'text-green-600' : 'text-red-600'}`}>
                    {classroom.actual_ratio}
                  </span>
                </div>
              </div>

              {/* Staff Names */}
              {((classroom.staff_members && classroom.staff_members.length > 0) || (classroom.staff_names && classroom.staff_names.length > 0)) && (
                <div className="mt-2 ml-6">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {(classroom.staff_members || classroom.staff_names.map(n => ({ id: null, name: n, photo_url: null }))).map((m, idx) => (
                      <div
                        key={m.id || idx}
                        className="flex items-center gap-1.5 bg-white/80 rounded-full pl-0.5 pr-2 py-0.5 border"
                        data-testid={`staff-chip-${m.id || idx}`}
                      >
                        {m.photo_url ? (
                          <img
                            src={`${API}${m.photo_url}`.replace('/api/api/', '/api/')}
                            alt={m.name}
                            className="w-5 h-5 rounded-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div
                          className="w-5 h-5 rounded-full bg-primary/10 items-center justify-center"
                          style={{ display: m.photo_url ? 'none' : 'flex' }}
                        >
                          <UserCheck className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-[11px] font-medium">{m.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Alert for understaffed */}
              {!classroom.is_compliant && classroom.staff_needed > 0 && (
                <div className="mt-2 ml-6 flex items-center gap-1 text-xs text-red-600 font-medium">
                  <AlertTriangle className="w-3 h-3" />
                  Need {classroom.staff_needed} more staff (DCF {classroom.dcf_primary_ratio})
                </div>
              )}
            </div>
          ))}
        </div>

        {/* View Schedule Link */}
        <Button 
          variant="outline" 
          className="w-full mt-2"
          onClick={() => window.location.href = '/staff/schedule'}
          data-testid="view-schedule-btn"
        >
          <Clock className="w-4 h-4 mr-2" />
          View Full Schedule
          <ChevronRight className="w-4 h-4 ml-auto" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default StaffRatioWidget;
